import t from 'react-translate';
import { useCallback, useContext } from 'react';
import { AngularServicesContext } from 'react-app';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { css } from '@emotion/core';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { copyFolder, moveFolder } from 'redux/actions/collections';
import { RootState } from 'redux/schemas';
import { getCollectionFolder } from 'redux/selectors/collections';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import CollectionListModalBody,
{
  CollectionListModalType,
  ModalActionType,
} from 'courses/components/course-lesson-to-collection/collection-list-modal-body';
import { FolderTransferModalActionType } from '../folder';

type FolderTransferModalProps = {
  show: boolean,
  setShow: Function,
  folderTransferModalType: FolderTransferModalActionType,
  currentFolderId?: number,
  currentCatalogId?: string,
  setCollectionFolderIds?: Function
  hasExternalSubmissionLinks?: boolean,
};

const modalStyles = css`
  .bs4-modal-body {
    overflow-y: clip;
  }
`;

const FolderTransferModal = (props: FolderTransferModalProps) => {
  const {
    show,
    setShow,
    folderTransferModalType,
    currentFolderId,
    currentCatalogId,
    setCollectionFolderIds,
    hasExternalSubmissionLinks,
  } = props;

  const { $state } = useContext(AngularServicesContext);
  const dispatch = useDispatch();

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      destinationCollectionName: null,
      destinationCollectionId: null,
      destinationCatalogId: null,
    },
  });

  const { reset, getValues } = methods;

  const currentFolder = useSelector((state: RootState) => getCollectionFolder(state, currentFolderId));

  const getHeader = () => {
    switch (folderTransferModalType) {
      case FolderTransferModalActionType.COPY_FOLDER:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_FOLDER_HEADER();
      case FolderTransferModalActionType.MOVE_FOLDER:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_FOLDER_HEADER();
      default:
        return null;
    }
  };

  const getDescription = () => {
    switch (folderTransferModalType) {
      case FolderTransferModalActionType.COPY_FOLDER:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_FOLDER_DESCRIPTION();
      case FolderTransferModalActionType.MOVE_FOLDER:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_FOLDER_DESCRIPTION();
      default:
        return null;
    }
  };

  const getModalActionType = () => {
    switch (folderTransferModalType) {
      case FolderTransferModalActionType.COPY_FOLDER:
        return ModalActionType.COPY;
      case FolderTransferModalActionType.MOVE_FOLDER:
        return ModalActionType.MOVE;
      default:
        return null;
    }
  };

  const handleClose = useCallback(() => {
    setShow(false);
    reset();
  }, [setShow, reset]);

  const redirectToCollectionFolder = useCallback((stateParams) => {
    window.open($state?.href(
      'content-library-collection-home',
      stateParams,
    ), '_blank');
  }, [$state]);

  const moveFolderDispatchAction = useCallback(() => {
    dispatch(moveFolder({
      sourceCatalogId: currentCatalogId,
      destinationCatalogId: getValues('destinationCatalogId'),
      folderId: currentFolderId,
    })).then((res) => {
      if (!res.error) {
        setCollectionFolderIds((prevList) => {
          const newList = [...prevList];
          const index = newList.indexOf(currentFolderId);
          if (index > -1) {
            newList.splice(index, 1);
          }
          return newList;
        });
        const destinationCatalogId = getValues('destinationCatalogId');
        const destinationCollectionId = getValues('destinationCollectionId');
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_FOLDER_SUCCESS_MSG(currentFolder?.title),
          button: (
            <Button
              onClick={() => redirectToCollectionFolder(
                {
                  collectionId: destinationCollectionId,
                  catalogId: destinationCatalogId,
                  folderId: currentFolderId,
                },
              )}
            >
              {t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.GO_TO_COLLECTION()}
            </Button>
          ),
        }));
      }
      handleClose();
    });
  }, [
    dispatch,
    getValues,
    currentFolderId,
    currentCatalogId,
    currentFolder?.title,
    handleClose,
    redirectToCollectionFolder,
    setCollectionFolderIds,
  ]);

  const openAssignmentWarningModal = useCallback(() => {
    dispatch(openConfirmationDialog({
      title: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.SUBMISSION_CAROUSAL_FOLDER_TRANSFER_INFO_HEADER(),
      bodyText: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.SUBMISSION_CAROUSAL_FOLDER_TRANSFER_INFO_DESCRIPTION(),
      confirmText: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.GOT_IT(),
      icon: 'success',
      noCancelButton: true,
      confirmButtonVariant: 'primary',
      onConfirm: () => {
        moveFolderDispatchAction();
      },
    }));
  }, [dispatch, moveFolderDispatchAction]);

  const submitAction = useCallback(() => {
    switch (folderTransferModalType) {
      case FolderTransferModalActionType.COPY_FOLDER:

        dispatch(copyFolder({
          destinationCatalogId: getValues('destinationCatalogId'),
          folderId: currentFolderId,
        })).then((res) => {
          if (!res.error) {
            const destinationCollectionName = getValues('destinationCollectionName');
            dispatch(addAlertMessage({
              type: AlertMessageType.INFO,
              message: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_FOLDER_STARTED(destinationCollectionName, currentFolder?.title),
            }));
          }
          handleClose();
        });
        break;
      case FolderTransferModalActionType.MOVE_FOLDER:
      {
        if (hasExternalSubmissionLinks) {
          openAssignmentWarningModal();
        } else {
          moveFolderDispatchAction();
        }
        break;
      }
      default:
        break;
    }
  }, [
    folderTransferModalType,
    dispatch,
    getValues,
    currentFolderId,
    currentFolder?.title,
    handleClose,
    hasExternalSubmissionLinks,
    moveFolderDispatchAction,
    openAssignmentWarningModal,
  ]);

  return (
    <div>
      <NvModal
        width={800}
        show={show}
        bodyClassName='p-0'
        type={ModalType.DYNAMIC}
        onClose={handleClose}
        header={getHeader()}
        modalStyles={modalStyles}
        fullHeight={false}
        body={(
          <FormProvider {...methods}>
            <form>
              <CollectionListModalBody
                bodyDescription={getDescription()}
                collectionListModalType={CollectionListModalType.FOLDER_TRANSFER}
                modalActionType={getModalActionType()}
                submitAction={submitAction}
                currentCatalogId={currentCatalogId}
                onClose={handleClose}
              />
            </form>
          </FormProvider>
        )}
      />
    </div>
  );
};

export default FolderTransferModal;
